"use client";

import { Button } from "@/components/rac/button";
import { DialogContent, DialogOverlay } from "@/components/rac/dialog";
import { SheetContent, SheetOverlay } from "@/components/rac/sheet";
import { SkeletonLoader } from "@/components/ui/skeleton";
import { useHPIData } from "@/hooks/use-hpi-data";
import { useIsMobile } from "@/hooks/use-mobile";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import { useState } from "react";
import { type ComparablePrice } from "../optimization/types";

interface ModalContentProps {
	comparablePrices?: ComparablePrice[];
	onClose: () => void;
	RBI_PRICE_INDEX_DATA: ReturnType<typeof useHPIData>["data"];
	isHPITableExpanded: boolean;
	setIsHPITableExpanded: (value: boolean) => void;
}

const ModalContent = ({
	comparablePrices,
	onClose,
	RBI_PRICE_INDEX_DATA,
	isHPITableExpanded,
	setIsHPITableExpanded,
}: ModalContentProps) => {
	// Find the index of the quarter when the property was sold
	const soldQuarter = comparablePrices?.[0]?.indexMultiplierKey || "2010-1";
	const soldIndex = RBI_PRICE_INDEX_DATA?.findIndex(data => data.quarter === soldQuarter) ?? 0;

	return (
		<div>
			<div className="flex justify-between items-center mb-4">
				<h2 className="text-xl font-semibold">Price Adjustment</h2>
				<Button variant="bareshell" onPress={onClose} className="p-1">
					<X className="h-5 w-5 text-gray-500" />
				</Button>
			</div>
			{comparablePrices?.[0]?.steps.map(step => (
				<li key={step}>{step}</li>
			))}
			{/* Explanation and HPI Toggle */}
			<div className="mt-2 space-y-3">
				<div className="flex justify-center">
					<button
						onClick={() => setIsHPITableExpanded(!isHPITableExpanded)}
						className="inline-flex items-center gap-1.5 px-4 py-2 text-xs sm:text-sm text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50 font-medium transition-all cursor-pointer rounded-lg border border-emerald-200 shadow-sm hover:shadow-md"
						aria-label={isHPITableExpanded ? "Hide HPI details" : "Show HPI details"}
					>
						{isHPITableExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
						<span>{isHPITableExpanded ? "Hide" : "View"} Historical Price Index</span>
					</button>
				</div>
			</div>
			{/* Expandable HPI Table */}
			<div
				className={`overflow-hidden transition-all duration-300 ease-in-out ${
					isHPITableExpanded ? "max-h-[400px] opacity-100 mt-4" : "max-h-0 opacity-0 mt-0"
				}`}
			>
				<div className="pt-4 border-t border-emerald-200">
					{RBI_PRICE_INDEX_DATA && (
						<div className="max-h-64 overflow-y-auto border border-gray-200 rounded">
							<table className="w-full text-xs">
								<thead className="bg-gray-50 sticky top-0">
									<tr>
										<th className="text-left px-3 py-2 font-medium text-gray-700 border-b">Quarter</th>
										<th className="text-right px-3 py-2 font-medium text-gray-700 border-b">Index</th>
									</tr>
								</thead>
								<tbody>
									{RBI_PRICE_INDEX_DATA.slice(soldIndex).map((data, index) => {
										const slicedLength = RBI_PRICE_INDEX_DATA.slice(soldIndex).length;
										return (
											<tr
												key={data.quarter}
												className={
													index === 0 || index === slicedLength - 1
														? "bg-emerald-50 font-medium"
														: index % 2 === 0
															? "bg-white"
															: "bg-gray-50"
												}
											>
												<td className="px-3 py-2 border-b text-gray-900">
													{data.quarter}
													{index === slicedLength - 1 && <span className="ml-1 text-emerald-600">(Latest)</span>}
													{index === 0 && <span className="ml-1 text-blue-600">(Sold)</span>}
												</td>
												<td className="px-3 py-2 border-b text-right text-gray-900">{data.value}</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

interface HPIInfoModalProps {
	isOpen: boolean;
	onClose: () => void;
	comparablePrices?: ComparablePrice[];
}
export function HPIInfoModal({ isOpen, onClose, comparablePrices }: HPIInfoModalProps) {
	const [isHPITableExpanded, setIsHPITableExpanded] = useState(false);
	const isMobile = useIsMobile();

	// Only fetch HPI data when modal is open (skip when closed for efficiency)
	const { data: RBI_PRICE_INDEX_DATA, loading } = useHPIData(!isOpen);

	// Don't render anything if modal is not open
	if (!isOpen) return null;

	// Show loading state inside the modal
	const modalContentOrLoading = loading ? (
		<div className="flex items-center justify-center py-12">
			<SkeletonLoader height="200px" width="100%" />
		</div>
	) : (
		<ModalContent
			comparablePrices={comparablePrices}
			onClose={onClose}
			RBI_PRICE_INDEX_DATA={RBI_PRICE_INDEX_DATA}
			isHPITableExpanded={isHPITableExpanded}
			setIsHPITableExpanded={setIsHPITableExpanded}
		/>
	);

	// Render as bottom sheet on mobile, dialog on desktop
	if (isMobile) {
		return (
			<SheetOverlay isOpen={isOpen} onOpenChange={(open: boolean) => !open && onClose()}>
				<SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto rounded-t-2xl p-4" closeButton={false}>
					{modalContentOrLoading}
				</SheetContent>
			</SheetOverlay>
		);
	}

	return (
		<DialogOverlay isOpen={isOpen} onOpenChange={(open: boolean) => !open && onClose()}>
			<DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" closeButton={false}>
				{modalContentOrLoading}
			</DialogContent>
		</DialogOverlay>
	);
}
